/**
 * TierRestrictedToolBlock - Shown when a tier-restricted user triggers a tool
 * that requires a higher subscription (e.g. Clinician plan). Displays the
 * backend error message and an upgrade button that opens the Clinician upgrade
 * modal (which already handles team-member permission logic).
 */

import { usePaywallStates } from '@/atoms/paywallAtoms';
import { useEvidenceAnalytics } from '@/hooks/useEvidenceAnalytics';
import { Button } from '@heidi/design-system/button';
import {
  TypographyCaption,
  TypographyP2Bold,
} from '@heidi/design-system/typography';
import { cn } from '@/lib/cn';
import { isAppleCompliantBuild } from '@/utils/appConfiguration';
import { isOnPrem } from '@/utils/buildProfile';
import { useTranslations } from '@heidi/runtime/i18n';
import { Lock } from 'lucide-react';
import { useEffect, useRef, type JSX } from 'react';

import { formatToolName } from '@heidi/evidence/data';

export interface TierRestrictedToolBlockProps {
  message: string;
  toolName?: string;
  className?: string;
}

export const TierRestrictedToolBlock = ({
  message,
  toolName,
  className,
}: TierRestrictedToolBlockProps): JSX.Element => {
  const { t } = useTranslations();
  const { toggleClinicianUpgradeModal } = usePaywallStates();
  const { trackChatError } = useEvidenceAnalytics();
  const errorTrackedRef = useRef(false);
  const displayName = toolName ? formatToolName(toolName) : null;

  useEffect(() => {
    if (!errorTrackedRef.current) {
      errorTrackedRef.current = true;
      trackChatError({
        errorType: 'block',
        errorMessage: message,
      });
    }
  }, [message, trackChatError]);

  return (
    <div
      className={cn(
        'flex w-full items-center gap-4 rounded-lg border border-border bg-surface-accent p-4',
        'transition-all duration-200',
        className
      )}
      data-testid="tier-restricted-tool-block"
    >
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <Lock
          className="size-5 shrink-0 text-text-tertiary"
          aria-hidden="true"
        />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          {displayName && (
            <TypographyP2Bold className="truncate text-text-primary">
              {displayName}
            </TypographyP2Bold>
          )}
          <TypographyCaption className="break-words text-text-secondary">
            {message}
          </TypographyCaption>
        </div>
      </div>
      {/* ON-PREM-GATE: an appliance is deployment-licensed — there is no plan to
          upgrade to and the upgrade modal is not mounted there (F6), so the
          button would be a dead click. Same term `use-upgrade-cta.ts` carries. */}
      {!isAppleCompliantBuild() && !isOnPrem && (
        <Button
          variant="default"
          size="md"
          onClick={() => toggleClinicianUpgradeModal(true)}
          data-testid="tier-restricted-tool-upgrade-button"
          className="shrink-0"
        >
          {t('quota.upgradeToClinician')}
        </Button>
      )}
    </div>
  );
};
